import { Accept, useDropzone } from "react-dropzone";
import { CirclePlusIcon } from "lucide-react";

import { OrChoices } from "@/components/molecules/OrChoices";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

export function FileDropzone({
  onDrop,
  accept,
  multiple = true,
  hint,
}: {
  onDrop: (acceptedFiles: File[]) => void;
  /**
   * Optional MIME-type / extension filter forwarded to ``react-dropzone``.
   * Use to restrict the picker (e.g. ``{ "text/csv": [".csv"] }`` for the
   * CSV import command). Omitting it preserves the legacy "accept
   * anything" behaviour callers like ``UploadFilesCommand`` rely on.
   */
  accept?: Accept;
  multiple?: boolean;
  hint?: string;
}) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple,
  });

  return (
    <>
      <div className="flex min-w-0 flex-wrap justify-center items-center p-2">
        <div
          className={cn(
            "p-8 rounded-md",
            isDragActive ? "border-foreground" : "border-border",
            "[border-style:dashed] cursor-pointer w-full",
          )}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <div className="min-w-0 flex-wrap flex flex-col justify-center items-center h-24">
            {isDragActive && (
              // As Material's icon drew under Joy's theme: Joy's icon colour,
              // at the size of the words around it.
              <CirclePlusIcon
                aria-hidden
                focusable="false"
                data-testid="AddCircleOutlineIcon"
                className="inline-block size-[1em] shrink-0 fill-current stroke-none [stroke-width:1px] select-none text-muted-foreground"
              />
            )}
            {!isDragActive && (
              <OrChoices>
                <Typography level="body-md" className="text-center">
                  {hint || "Drag and drop files to upload"}
                </Typography>
                <Button variant="outline" size="sm">
                  Browse Files
                </Button>
              </OrChoices>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
